import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, Divider, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  MASK,
  masked
} from '../common';
import {
  disconnectWorkdayApi,
  disconnectWorkdaySoap,
  saveWorkdayApiCredentials,
  saveWorkdaySoapCredentials,
  testWorkdayApiConnection,
  testWorkdaySoapConnection,
  useWorkdayApiStatus,
  useWorkdaySoapStatus,
  WorkdayApiCredentials,
  WorkdaySoapCredentials
} from './service';

const NAME = 'Workday API';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const PrivateKeyField = styled(TextField)({
  '& .MuiInputBase-inputMultiline': {
    fontFamily: 'monospace',
    fontSize: '12px'
  }
});

interface SharedState {
  tenant: string;
  isuUsername: string;
}

interface RestState {
  clientId: string;
  privateKey: string;
}

interface SoapState {
  isuPassword: string;
}

const isRestSaveValid = (shared: SharedState, rest: RestState): boolean => {
  return !!(
    shared.tenant.trim() &&
    shared.isuUsername.trim() &&
    rest.clientId.trim() &&
    rest.privateKey.trim()
  );
};

const isRestChanged = (shared: SharedState, rest: RestState, credentials: WorkdayApiCredentials | null): boolean => {
  if (!credentials) return true;
  return (
    shared.tenant !== credentials.tenant ||
    shared.isuUsername !== credentials.isuUsername ||
    rest.clientId !== credentials.clientId ||
    rest.privateKey !== MASK
  );
};

const isSoapSaveValid = (shared: SharedState, soap: SoapState): boolean => {
  return !!(
    shared.tenant.trim() &&
    shared.isuUsername.trim() &&
    soap.isuPassword.trim()
  );
};

const isSoapChanged = (shared: SharedState, soap: SoapState, credentials: WorkdaySoapCredentials | null): boolean => {
  if (!credentials) return true;
  return (
    shared.tenant !== credentials.tenant ||
    shared.isuUsername !== credentials.isuUsername ||
    soap.isuPassword !== MASK
  );
};

const SetupDialog = ({
  orgId,
  credentials,
  soapCredentials,
  open,
  onClose,
  onSuccessfulIntegration,
  onRefreshRest,
  onRefreshSoap
}: {
  orgId: string;
  credentials: WorkdayApiCredentials | null;
  soapCredentials: WorkdaySoapCredentials | null;
  open: boolean;
  onClose: () => void;
  onSuccessfulIntegration: () => void;
  onRefreshRest: () => void;
  onRefreshSoap: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  // Shared state (tenant + ISU username)
  const initialShared: SharedState = {
    tenant: credentials?.tenant ?? soapCredentials?.tenant ?? '',
    isuUsername: credentials?.isuUsername ?? soapCredentials?.isuUsername ?? ''
  };
  const [shared, setShared] = useState<SharedState>(initialShared);

  // REST-specific state
  const initialRest: RestState = {
    clientId: credentials?.clientId ?? '',
    privateKey: masked(credentials?.privateKey ?? '')
  };
  const [rest, setRest] = useState<RestState>(initialRest);

  // SOAP-specific state
  const initialSoap: SoapState = {
    isuPassword: masked(soapCredentials?.isuPassword ?? '')
  };
  const [soap, setSoap] = useState<SoapState>(initialSoap);

  // REST test state
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testError, setTestError] = useState<string | null>(null);
  const [showManualHost, setShowManualHost] = useState(false);
  const [manualHost, setManualHost] = useState('');

  // SOAP test state
  const [soapTestStatus, setSoapTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [soapTestError, setSoapTestError] = useState<string | null>(null);
  const [showSoapManualHost, setShowSoapManualHost] = useState(false);
  const [soapManualHost, setSoapManualHost] = useState('');

  // Sync state from loaded credentials
  useEffect(() => {
    setShared({
      tenant: credentials?.tenant ?? soapCredentials?.tenant ?? '',
      isuUsername: credentials?.isuUsername ?? soapCredentials?.isuUsername ?? ''
    });
    setRest({
      clientId: credentials?.clientId ?? '',
      privateKey: masked(credentials?.privateKey ?? '')
    });
  }, [credentials]);

  useEffect(() => {
    setSoap({
      isuPassword: masked(soapCredentials?.isuPassword ?? '')
    });
    // Also update shared if no REST credentials loaded yet
    if (!credentials) {
      setShared((s) => ({
        tenant: s.tenant || soapCredentials?.tenant || '',
        isuUsername: s.isuUsername || soapCredentials?.isuUsername || ''
      }));
    }
  }, [soapCredentials]);

  // Build full credential objects from shared + section-specific
  const buildRestCredentials = (apiHost?: string): WorkdayApiCredentials => ({
    ...shared,
    ...rest,
    ...(apiHost && { apiHost })
  });

  const buildSoapCredentials = (apiHost?: string): WorkdaySoapCredentials => ({
    ...shared,
    ...soap,
    ...(apiHost && { apiHost })
  });

  // REST handlers
  const handleRestSave = async () => {
    await saveWorkdayApiCredentials(orgId, buildRestCredentials());
    onRefreshRest();
    onSuccessfulIntegration();
    setTestStatus('testing');
    setTestError(null);
    setShowManualHost(false);
    try {
      await testWorkdayApiConnection(orgId);
      setTestStatus('success');
      onRefreshRest();
    } catch (err) {
      setTestStatus('error');
      setTestError(err instanceof Error ? err.message : 'Connection test failed');
      setShowManualHost(true);
    }
  };

  const handleRestTest = async () => {
    setTestStatus('testing');
    setTestError(null);
    setShowManualHost(false);
    try {
      await testWorkdayApiConnection(orgId);
      setTestStatus('success');
      onRefreshRest();
    } catch (err) {
      setTestStatus('error');
      setTestError(err instanceof Error ? err.message : 'Connection test failed');
      setShowManualHost(true);
    }
  };

  const handleRestSaveManualHost = async () => {
    if (!manualHost.trim()) return;
    await saveWorkdayApiCredentials(orgId, buildRestCredentials(manualHost.trim()));
    onRefreshRest();
    setTestStatus('testing');
    setTestError(null);
    try {
      await testWorkdayApiConnection(orgId);
      setTestStatus('success');
      setShowManualHost(false);
      onRefreshRest();
    } catch (err) {
      setTestStatus('error');
      setTestError(err instanceof Error ? err.message : 'Connection test failed');
    }
  };

  const handleRestDisconnect = async () => {
    await disconnectWorkdayApi(orgId);
    onRefreshRest();
  };

  // SOAP handlers
  const handleSoapSave = async () => {
    await saveWorkdaySoapCredentials(orgId, buildSoapCredentials());
    onRefreshSoap();
    onSuccessfulIntegration();
    setSoapTestStatus('testing');
    setSoapTestError(null);
    setShowSoapManualHost(false);
    try {
      await testWorkdaySoapConnection(orgId);
      setSoapTestStatus('success');
      onRefreshSoap();
    } catch (err) {
      setSoapTestStatus('error');
      setSoapTestError(err instanceof Error ? err.message : 'SOAP connection test failed');
      setShowSoapManualHost(true);
    }
  };

  const handleSoapTest = async () => {
    setSoapTestStatus('testing');
    setSoapTestError(null);
    setShowSoapManualHost(false);
    try {
      await testWorkdaySoapConnection(orgId);
      setSoapTestStatus('success');
      onRefreshSoap();
    } catch (err) {
      setSoapTestStatus('error');
      setSoapTestError(err instanceof Error ? err.message : 'SOAP connection test failed');
      setShowSoapManualHost(true);
    }
  };

  const handleSoapSaveManualHost = async () => {
    if (!soapManualHost.trim()) return;
    await saveWorkdaySoapCredentials(orgId, buildSoapCredentials(soapManualHost.trim()));
    onRefreshSoap();
    setSoapTestStatus('testing');
    setSoapTestError(null);
    try {
      await testWorkdaySoapConnection(orgId);
      setSoapTestStatus('success');
      setShowSoapManualHost(false);
      onRefreshSoap();
    } catch (err) {
      setSoapTestStatus('error');
      setSoapTestError(err instanceof Error ? err.message : 'SOAP connection test failed');
    }
  };

  const handleSoapDisconnect = async () => {
    await disconnectWorkdaySoap(orgId);
    onRefreshSoap();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t(
            'Connect Dado to Workday to enable syncing data from Dado back to Workday.'
          )}
        </Alert>

        {/* ============= Shared Fields ============= */}
        <FormRow>
          <CustomInputLabel label={t('Tenant')} fullWidth>
            <TextField
              variant="outlined"
              value={shared.tenant}
              onChange={(ev) =>
                setShared((s) => ({ ...s, tenant: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>

        <FormRow>
          <CustomInputLabel label={t('ISU Username')} fullWidth>
            <TextField
              variant="outlined"
              value={shared.isuUsername}
              onChange={(ev) =>
                setShared((s) => ({ ...s, isuUsername: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>

        {/* ============= REST API Section ============= */}
        <Divider style={{ margin: '8px 0' }} />
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {t('REST API')}
        </Typography>

        <FormRow>
          <CustomInputLabel label={t('Client ID')} fullWidth>
            <TextField
              variant="outlined"
              value={rest.clientId}
              onChange={(ev) =>
                setRest((s) => ({ ...s, clientId: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>

        <FormRow>
          <CustomInputLabel label={t('Private Key')} fullWidth>
            <PrivateKeyField
              variant="outlined"
              value={rest.privateKey}
              onChange={(ev) =>
                setRest((s) => ({ ...s, privateKey: ev.target.value }))
              }
              fullWidth
              multiline
              rows={6}
              placeholder={'-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----'}
            />
          </CustomInputLabel>
        </FormRow>

        <FormRow style={{ display: 'flex', gap: 8 }}>
          <ButtonWithPromise
            variant="contained"
            color="primary"
            onClick={handleRestSave}
            disabled={!isRestSaveValid(shared, rest) || !isRestChanged(shared, rest, credentials)}
            pending={`${t('Saving')}...`}
          >
            {t('Save REST Credentials')}
          </ButtonWithPromise>
          {credentials && (
            <ButtonWithPromise
              variant="outlined"
              color="primary"
              onClick={handleRestTest}
              pending={`${t('Testing')}...`}
              disabled={testStatus === 'testing'}
            >
              {t('Test Connection')}
            </ButtonWithPromise>
          )}
        </FormRow>

        {testStatus === 'testing' && (
          <Alert severity="info">
            {t('Testing REST connection...')}
          </Alert>
        )}
        {testStatus === 'success' && (
          <Alert severity="success">
            {t('REST connection successful!')}
          </Alert>
        )}
        {testStatus === 'error' && (
          <Alert severity="error">
            {testError || t('REST connection failed')}
          </Alert>
        )}

        {showManualHost && (
          <FormRow>
            <CustomInputLabel label={t('Workday Host')} fullWidth>
              <TextField
                variant="outlined"
                value={manualHost}
                onChange={(ev) => setManualHost(ev.target.value)}
                fullWidth
                placeholder="wd5-services1.workday.com"
                helperText={t('Could not auto-detect your Workday datacenter. Find the hostname in your browser URL bar when logged into Workday.')}
              />
            </CustomInputLabel>
            <ButtonWithPromise
              variant="outlined"
              color="primary"
              onClick={handleRestSaveManualHost}
              pending={`${t('Testing')}...`}
              disabled={!manualHost.trim() || testStatus === 'testing'}
              style={{ marginTop: 8 }}
            >
              {t('Retry with this host')}
            </ButtonWithPromise>
          </FormRow>
        )}

        {credentials && (
          <FormRow>
            <ButtonWithPromise
              variant="outlined"
              color="secondary"
              onClick={handleRestDisconnect}
              pending={`${t('Removing')}...`}
            >
              {t('Remove REST Credentials')}
            </ButtonWithPromise>
          </FormRow>
        )}

        {/* ============= SOAP API Section ============= */}
        <Divider style={{ margin: '8px 0' }} />
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {t('SOAP API')}
        </Typography>

        <FormRow>
          <CustomInputLabel label={t('ISU Password')} fullWidth>
            <TextField
              variant="outlined"
              type="password"
              value={soap.isuPassword}
              onChange={(ev) =>
                setSoap((s) => ({ ...s, isuPassword: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>

        <FormRow style={{ display: 'flex', gap: 8 }}>
          <ButtonWithPromise
            variant="contained"
            color="primary"
            onClick={handleSoapSave}
            disabled={!isSoapSaveValid(shared, soap) || !isSoapChanged(shared, soap, soapCredentials)}
            pending={`${t('Saving')}...`}
          >
            {t('Save SOAP Credentials')}
          </ButtonWithPromise>
          {soapCredentials && (
            <ButtonWithPromise
              variant="outlined"
              color="primary"
              onClick={handleSoapTest}
              pending={`${t('Testing')}...`}
              disabled={soapTestStatus === 'testing'}
            >
              {t('Test Connection')}
            </ButtonWithPromise>
          )}
        </FormRow>

        {soapTestStatus === 'testing' && (
          <Alert severity="info">
            {t('Testing SOAP connection...')}
          </Alert>
        )}
        {soapTestStatus === 'success' && (
          <Alert severity="success">
            {t('SOAP connection successful!')}
          </Alert>
        )}
        {soapTestStatus === 'error' && (
          <Alert severity="error">
            {soapTestError || t('SOAP connection failed')}
          </Alert>
        )}

        {showSoapManualHost && (
          <FormRow>
            <CustomInputLabel label={t('Workday Host')} fullWidth>
              <TextField
                variant="outlined"
                value={soapManualHost}
                onChange={(ev) => setSoapManualHost(ev.target.value)}
                fullWidth
                placeholder="wd5-services1.workday.com"
                helperText={t('Could not auto-detect your Workday datacenter. Find the hostname in your browser URL bar when logged into Workday.')}
              />
            </CustomInputLabel>
            <ButtonWithPromise
              variant="outlined"
              color="primary"
              onClick={handleSoapSaveManualHost}
              pending={`${t('Testing')}...`}
              disabled={!soapManualHost.trim() || soapTestStatus === 'testing'}
              style={{ marginTop: 8 }}
            >
              {t('Retry with this host')}
            </ButtonWithPromise>
          </FormRow>
        )}

        {soapCredentials && (
          <FormRow>
            <ButtonWithPromise
              variant="outlined"
              color="secondary"
              onClick={handleSoapDisconnect}
              pending={`${t('Removing')}...`}
            >
              {t('Remove SOAP Credentials')}
            </ButtonWithPromise>
          </FormRow>
        )}
      </Content>
      <DialogActionsWithSlots
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

export const WorkdayApiSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { org } = useCurrentUser();
  const { status: restStatus, refresh: refreshRest } = useWorkdayApiStatus(org.id);
  const { status: soapStatus, refresh: refreshSoap } = useWorkdaySoapStatus(org.id);

  const isActive = (restStatus?.connected ?? false) || (soapStatus?.connected ?? false);

  return (
    <IntegrationConfigurationButton
      name={NAME}
      functionalities={functionalities}
      logoUrl="/images/integrations-logos/workday.svg"
      active={isActive}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          orgId={org.id}
          credentials={restStatus?.credentials ?? null}
          soapCredentials={soapStatus?.credentials ?? null}
          open={open}
          onClose={onClose}
          onSuccessfulIntegration={onSuccessfulIntegration}
          onRefreshRest={refreshRest}
          onRefreshSoap={refreshSoap}
        />
      )}
    />
  );
};
